/**
 * Magazine parallax
 * -----------------
 * Opt-in per Magazine section (setting "Parallax" → data-parallax on .magazine).
 * Banner images drift up slightly faster than the page scrolls, with a soft stagger:
 *  - every layer eases toward its target, so motion trails the scroll a little
 *  - in a multi-slide row, the second tile moves further and settles later
 *  - overlay text moves less than its image and lags a little more behind it
 *
 * Each layer gets a --parallax custom property (px); the transforms live in
 * src/scss/blocks/_superfluid.scss. Loaded as a module, so it runs once no
 * matter how many Magazine sections include it.
 */

const SECTION_SELECTOR = '.magazine[data-parallax]';
const SLIDE_SELECTOR = '.superfluid__slide';
const IMAGE_SELECTOR = 'picture img';
const CONTENT_SELECTOR = '.slide__content-wrapper';
const OVERLAY_MOBILE_CLASS = 'layout--overlay';
const OVERLAY_DESKTOP_CLASS = 'layout-desktop--overlay';
const UP_ONLY_CLASS = 'magazine--parallax-up';
const PARALLAX_VAR = '--parallax';
const SCALE_VAR = '--parallax-scale';

// Max shift as a fraction of image height. strength × speed must stay within
// the overhang given by --parallax-scale in CSS: (scale - 1) / 2.
const STRENGTH_MOBILE = 0.1;
const STRENGTH_DESKTOP = 0.15;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktop = window.matchMedia('(min-width: 768px)');

let layers = [];
let running = false;
let framingDirty = true;

function strength() {
  return desktop.matches ? STRENGTH_DESKTOP : STRENGTH_MOBILE;
}

function collectLayers() {
  layers = [];
  document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
    const slides = section.querySelectorAll(SLIDE_SELECTOR);
    const inRow = slides.length > 1;
    // Single full-width banners (class set in Liquid) only drift upward.
    const upOnly = section.classList.contains(UP_ONLY_CLASS);

    slides.forEach((slide, index) => {
      const second = inRow && index > 0;
      const img = slide.querySelector(IMAGE_SELECTOR);
      if (img) {
        layers.push({
          el: img,
          box: img.parentElement,
          speed: second ? 1.1 : inRow ? 0.8 : 1,
          ease: second ? 0.08 : 0.12,
          upOnly,
          isImage: true,
          current: 0,
          target: 0,
        });
      }

      const content = slide.querySelector(CONTENT_SELECTOR);
      if (content) {
        layers.push({
          el: content,
          box: slide,
          speed: 0.3,
          ease: 0.06,
          // Only overlaid text floats; stacked (image-left/above/…) text stays in flow.
          isContent: true,
          slide,
          current: 0,
          target: 0,
        });
      }
    });
  });
}

// How far an image can shift each way before an edge shows. The zoom is
// anchored on transform-origin (the Shopify focal point, or the CSS default),
// so the overhang splits around it: origin at fraction f from the top leaves
// f·(scale-1)·h above and (1-f)·(scale-1)·h below. Re-read on resize, since
// scale, focal point (mobile vs desktop image) and height change by breakpoint.
function readFraming() {
  layers.forEach((l) => {
    if (!l.isImage) return;
    const cs = getComputedStyle(l.el);
    const scale = parseFloat(cs.getPropertyValue(SCALE_VAR)) || 1;
    const h = l.el.offsetHeight;
    const originY = parseFloat(cs.transformOrigin.split(' ')[1]);
    const f = h > 0 && Number.isFinite(originY) ? Math.min(1, Math.max(0, originY / h)) : 0.5;
    const overhang = (scale - 1) * h;
    l.maxUp = (1 - f) * overhang;
    l.maxDown = f * overhang;
  });
  framingDirty = false;
}

function measure() {
  if (framingDirty) readFraming();
  const vh = window.innerHeight;
  layers.forEach((l) => {
    const box = l.box.getBoundingClientRect();
    l.visible = box.bottom > -200 && box.top < vh + 200;
    if (!l.visible) return;

    if (l.isContent) {
      const overlayClass = desktop.matches ? OVERLAY_DESKTOP_CLASS : OVERLAY_MOBILE_CLASS;
      if (!l.slide.classList.contains(overlayClass)) {
        l.target = 0;
        return;
      }
    }

    // -1 when the banner is entering at the bottom, +1 as it leaves at the top
    let progress = 1 - (box.top + box.height) / ((vh + box.height) / 2);
    progress = Math.max(-1, Math.min(1, progress));
    l.target = -progress * strength() * l.speed * box.height;
    if (l.upOnly) l.target = Math.min(0, l.target);
    if (l.isImage) l.target = Math.max(-l.maxUp, Math.min(l.maxDown, l.target));
  });
}

function frame() {
  let moving = false;
  layers.forEach((l) => {
    if (!l.visible) return;
    const delta = l.target - l.current;
    if (Math.abs(delta) > 0.1) {
      l.current += delta * l.ease;
      moving = true;
    } else {
      l.current = l.target;
    }
    l.el.style.setProperty(PARALLAX_VAR, `${l.current.toFixed(2)}px`);
  });
  if (moving) requestAnimationFrame(frame);
  else running = false;
}

function request() {
  measure();
  if (!running) {
    running = true;
    requestAnimationFrame(frame);
  }
}

// Start settled, without an ease-in on page load.
function settle() {
  measure();
  layers.forEach((l) => {
    l.current = l.target;
    l.el.style.setProperty(PARALLAX_VAR, `${l.current.toFixed(2)}px`);
  });
}

function init() {
  collectLayers();
  framingDirty = true;
  settle();
}

if (!reducedMotion.matches) {
  init();
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', () => {
    framingDirty = true;
    request();
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
  }
}
